import { useEffect, useState, useCallback } from 'react'
import { Target, Plus, RefreshCw, Trash2, X, ExternalLink, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Test {
  id: number
  technique: string
  technique_name: string
  test_name: string
  platform: string
  executed_at: string
  executed_by: string
  outcome: string
  detection_source: string
  notes: string
  evidence_url: string | null
}

interface Rollup {
  technique: string
  total: number
  detected: number
  missed: number
  blocked: number
  last_run: string | null
}

const OUTCOMES = ['passed', 'detected', 'missed', 'blocked', 'errored'] as const
const PLATFORMS = ['windows', 'linux', 'macos', 'cloud', 'container']

const OUTCOME_TONE: Record<string, { icon: any; color: string }> = {
  passed:   { icon: CheckCircle2, color: 'text-emerald' },
  detected: { icon: CheckCircle2, color: 'text-emerald' },
  blocked:  { icon: CheckCircle2, color: 'text-emerald' },
  missed:   { icon: XCircle,      color: 'text-rose' },
  errored:  { icon: AlertTriangle, color: 'text-amber-light' },
}

export default function AtomicRedTeamPage() {
  const [tests, setTests] = useState<Test[]>([])
  const [rollup, setRollup] = useState<Rollup[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [filter, setFilter] = useState<string>('all')
  const [form, setForm] = useState({
    technique: '', technique_name: '', test_name: '', platform: 'windows',
    executed_at: new Date().toISOString().slice(0, 16),
    executed_by: '', outcome: 'detected', detection_source: '', notes: '',
  })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/sec/atomic-red-tests', { params: { limit: 200 } })
      setTests(r.data?.tests || [])
      setRollup(r.data?.by_technique || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.technique || !form.test_name) return
    try {
      await api.post('/api/sec/atomic-red-tests', {
        ...form,
        executed_at: form.executed_at ? new Date(form.executed_at).toISOString() : undefined,
      })
      setShowForm(false)
      setForm({ ...form, technique: '', test_name: '', notes: '' })
      fetchAll()
    } catch {}
  }

  const remove = async (id: number) => {
    if (!confirm('Delete this test record?')) return
    await api.delete(`/api/sec/atomic-red-tests/${id}`)
    fetchAll()
  }

  const filtered = filter === 'all' ? tests : tests.filter(t => t.outcome === filter)

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Target size={24} className="text-rose" /> Atomic Red Team Tracker
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Log Atomic Red Team test runs per ATT&CK technique. Did your detections fire? Build a verified-coverage record.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs">
            <Plus size={12} /> Log test
          </button>
        </div>
      </div>

      {/* Rollup by technique */}
      {rollup.length > 0 && (
        <div className="card p-3">
          <h3 className="text-xs font-semibold text-text-primary mb-2">Coverage by technique</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {rollup.slice(0, 18).map(r => {
              const detRate = r.total > 0 ? Math.round(((r.detected + r.blocked) / r.total) * 100) : 0
              return (
                <div key={r.technique} className="rounded p-2 border border-border bg-surface-card">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-text-primary">{r.technique}</span>
                    <span className={clsx('text-xs font-mono font-bold',
                      detRate >= 75 ? 'text-emerald' : detRate >= 50 ? 'text-amber-light' : 'text-rose')}>
                      {detRate}%
                    </span>
                  </div>
                  <div className="text-[10px] text-text-muted mt-0.5">
                    {r.total} runs · ✓{r.detected + r.blocked} · ✗{r.missed}
                    {r.last_run && <> · last {new Date(r.last_run).toLocaleDateString()}</>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Log atomic test</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.technique} onChange={e => setForm({ ...form, technique: e.target.value })}
              placeholder="Technique e.g. T1059.001" className="input py-1.5 font-mono" />
            <input value={form.technique_name} onChange={e => setForm({ ...form, technique_name: e.target.value })}
              placeholder="Technique name e.g. PowerShell" className="input py-1.5" />
            <input value={form.test_name} onChange={e => setForm({ ...form, test_name: e.target.value })}
              placeholder="Test name e.g. Atomic T1059.001 #2" className="input py-1.5 md:col-span-2" />
            <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })} className="input py-1.5">
              {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={form.outcome} onChange={e => setForm({ ...form, outcome: e.target.value })} className="input py-1.5">
              {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <label className="space-y-0.5">
              <span className="text-text-muted">Executed at</span>
              <input type="datetime-local" value={form.executed_at}
                onChange={e => setForm({ ...form, executed_at: e.target.value })}
                className="input w-full py-1.5" />
            </label>
            <input value={form.executed_by} onChange={e => setForm({ ...form, executed_by: e.target.value })}
              placeholder="Executed by" className="input py-1.5" />
            <input value={form.detection_source} onChange={e => setForm({ ...form, detection_source: e.target.value })}
              placeholder="Detection source e.g. Defender, Splunk, missed" className="input py-1.5 md:col-span-2" />
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
              placeholder="Notes" className="input py-1.5 md:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      {/* Outcome filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setFilter('all')}
          className={clsx('badge text-[10px] px-2 py-1', filter === 'all' ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
          All ({tests.length})
        </button>
        {OUTCOMES.map(o => {
          const n = tests.filter(t => t.outcome === o).length
          return (
            <button key={o} type="button" onClick={() => setFilter(o === filter ? 'all' : o)}
              className={clsx('badge text-[10px] px-2 py-1',
                filter === o ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
              {o} <span className="text-text-muted ml-1">{n}</span>
            </button>
          )
        })}
      </div>

      {/* Tests table */}
      <div className="card p-3 overflow-x-auto">
        {filtered.length === 0 ? (
          <p className="text-center py-6 text-text-muted text-[11px] italic">No tests logged yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                <th className="text-left py-2 px-2">Technique</th>
                <th className="text-left py-2 px-2">Test</th>
                <th className="text-left py-2 px-2">Platform</th>
                <th className="text-left py-2 px-2">Outcome</th>
                <th className="text-left py-2 px-2">Detection</th>
                <th className="text-left py-2 px-2">Executed</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => {
                const tone = OUTCOME_TONE[t.outcome] || OUTCOME_TONE.errored
                const Icon = tone.icon
                return (
                  <tr key={t.id} className="border-b border-border/50 hover:bg-surface-hover">
                    <td className="py-1.5 px-2 font-mono text-blue-light">{t.technique}</td>
                    <td className="py-1.5 px-2 text-text-secondary">{t.test_name}</td>
                    <td className="py-1.5 px-2 font-mono text-[10px] text-text-muted">{t.platform}</td>
                    <td className="py-1.5 px-2">
                      <span className={clsx('inline-flex items-center gap-1 text-[11px]', tone.color)}>
                        <Icon size={10} /> {t.outcome}
                      </span>
                    </td>
                    <td className="py-1.5 px-2 text-text-secondary">{t.detection_source || '—'}</td>
                    <td className="py-1.5 px-2 text-[10px] text-text-muted">{t.executed_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="py-1.5 px-2 text-right space-x-2">
                      {t.evidence_url && (
                        <a href={t.evidence_url} target="_blank" rel="noreferrer" className="text-blue-light"><ExternalLink size={10} /></a>
                      )}
                      <button type="button" onClick={() => remove(t.id)} className="text-text-muted hover:text-rose"><Trash2 size={10} /></button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
